import { Button, Tooltip } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { CheckIcon, CrossIcon } from '../../assets/icons';
import { Spin } from '../Spin';

export type ActionButtonProps = {
  onClick: () => void;
  label: string;
  defaultIcon: React.ReactNode;
  statusDurationMs?: number;
  isDisabled?: boolean;
  tooltipText?: React.ReactNode;
};

export type ActionButtonComponent = {
  handleAction: (event: Promise<any>) => void;
};

type ActionButtonState = 'idle' | 'loading' | 'success' | 'error';

export const ActionButton = forwardRef<ActionButtonComponent, ActionButtonProps>(
  ({ onClick, statusDurationMs = 3000, defaultIcon, isDisabled, label, tooltipText }, ref) => {
    const [state, setState] = useState<ActionButtonState>('idle');
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setStateWithAutoReset = useCallback(
      (nextState: Extract<ActionButtonState, 'success' | 'error'>) => {
        setState(nextState);
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        resetTimerRef.current = setTimeout(() => {
          setState('idle');
        }, statusDurationMs);
      },
      [statusDurationMs],
    );

    useImperativeHandle(ref, () => ({
      handleAction: async (promise: Promise<any>) => {
        try {
          setState('loading');
          await promise;
          setStateWithAutoReset('success');
        } catch (_error) {
          setStateWithAutoReset('error');
        }
      },
    }));

    useEffect(
      () => () => {
        if (resetTimerRef.current) {
          clearTimeout(resetTimerRef.current);
        }
      },
      [],
    );

    const getIcon = () => {
      switch (state) {
        case 'loading':
          return <Spin />;
        case 'success':
          return <CheckIcon />;
        case 'error':
          return <CrossIcon />;
        case 'idle':
        default:
          return defaultIcon;
      }
    };

    return (
      <Tooltip
        title={tooltipText}
        componentsProps={{
          tooltip: {
            sx: (theme) => ({
              width: 200,
              padding: theme.spacing(1.25),
              margin: '0 !important',
            }),
          },
        }}
      >
        <span>
          <Button
            onClick={onClick}
            startIcon={getIcon()}
            disabled={isDisabled}
            variant="outlined"
            sx={(theme) => ({
              ...(isDisabled && {
                border: `1px solid ${theme.palette.common.light2} !important`,
              }),
            })}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  },
);
